import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Flame, Clock3, HeartHandshake, ArrowRight } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';

const HERO_IMG = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-08-01/8a8598f2b264.png';
const ABOUT_IMG = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-08-01/29603b24278d.png';

const VALUES = [
  { icon: Flame, title: 'Real Dum Cooking', copy: 'No pressure cookers, no shortcuts. Each handi is sealed with dough and slow-steamed until the rice and spices become one.' },
  { icon: Clock3, title: 'Made to Order', copy: 'We don\'t pre-cook trays and reheat. Your dabba starts when you order it, so it travels hot and fresh.' },
  { icon: HeartHandshake, title: 'Recipes, Not Shortcuts', copy: 'Every spice blend is built from scratch — no pre-mixed masalas, no compromises on flavor.' },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>Our Story: Dum-Cooked Biryani the Slow Way | Biryani2Go</title>
        <meta name="description" content="Why Biryani2Go cooks biryani the slow dum way: sealed handis, hand-layered basmati rice, made-to-order in Omaha & Council Bluffs. No shortcuts, no heat lamps." />
        <link rel="canonical" href="https://biryani2go.us/about" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://biryani2go.us"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Our Story",
                "item": "https://biryani2go.us/about"
              }
            ]
          }
        ` }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Biryani2Go",
            "description": "Biryani2Go serves authentic dum-cooked biryani and Indian food in Omaha and Council Bluffs. We seal every handi with dough and steam it low and slow—no shortcuts, no heat lamps.",
            "url": "https://biryani2go.us/about",
            "publisher": {
              "@type": "Organization",
              "name": "Biryani2Go",
              "url": "https://biryani2go.us"
            }
          }
        ` }} />
      </Helmet>

      <section className="relative bg-maroon py-24 md:py-32 overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-maroon to-maroon/40" />
        <div className="relative max-w-screen-xl mx-auto px-5 md:px-8 text-center">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="eyebrow text-marigold mb-4">
            Our Story
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display font-semibold text-4xl md:text-6xl lg:text-7xl text-white max-w-3xl mx-auto leading-tight tracking-[-0.01em]"
          >
            Our Story: Why We Cook Biryani the Slow Way
          </motion.h1>
        </div>
      </section>

      <section className="bg-surface py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <p className="eyebrow mb-3">How It Started</p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-maroon leading-tight mb-5">
              Biryani2Go started with one question: why does "fast" food have to mean rushed food?
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-2 max-w-[58ch] mb-4">
              Real dum biryani takes hours — the rice parboiled just right, the meat marinated overnight,
              everything layered by hand into a handi, sealed with dough, and left to steam low and slow
              until the flavors have nowhere left to go but into each other.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-ink-2 max-w-[58ch]">
              We built Biryani2Go to prove that takeout doesn't have to skip that process. Every dabba
              that leaves our kitchen goes through the same seal, the same steam, the same patience —
              just packed to travel. Check out <Link to="/menu" className="text-ember font-semibold underline">our full menu</Link> to see what's steaming right now.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="rounded-[2rem] overflow-hidden card-tin aspect-[4/5]"
          >
            <img src={ABOUT_IMG} alt="Sealed copper biryani handi with steam rising" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="bg-surface2 py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          <SectionHeading eyebrow="What We Believe" title="No shortcuts, ever." align="center" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="card-tin p-8 text-center"
              >
                <div className="w-14 h-14 rounded-full bg-ember/10 flex items-center justify-center mb-5 mx-auto">
                  <v.icon size={24} className="text-ember" />
                </div>
                <h3 className="font-display font-semibold text-xl text-maroon mb-2">{v.title}</h3>
                <p className="text-sm leading-relaxed text-ink-2">{v.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-maroon py-20 md:py-24">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-white mb-6">Ready to taste the difference?</h2>
          <Link to="/menu" className="btn-primary text-base">
            Order Your Dabba <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
